import { createJSONStorage } from 'jotai/utils';

type PersistedPayload<T> = {
  version: number;
  value: T;
};

export type PersistedStorageOptions<T> = {
  version: number;
  migrate?: (persisted: unknown, version: number) => T;
};

/**
 * Creates a versioned storage adapter for atomWithStorage.
 * Supports migration from older versions and handles invalid data gracefully.
 *
 * @example
 * ```ts
 * const storage = createPersistedStorage<MyState>({
 *   version: 2,
 *   migrate: (old, version) => {
 *     if (version === 1) return migrateV1toV2(old);
 *     return defaultState;
 *   },
 * });
 *
 * const myAtom = atomWithStorage('my-key', defaultState, storage);
 * ```
 */
export function createPersistedStorage<T>({ version, migrate }: PersistedStorageOptions<T>) {
  const baseStorage = createJSONStorage<PersistedPayload<T>>(() => localStorage);

  return {
    getItem: (key: string, initialValue: T) => {
      const stored = baseStorage.getItem(key, {
        version,
        value: initialValue,
      });
      if (!stored) {
        return initialValue;
      }

      if (stored.version === version) {
        return stored.value;
      }

      if (migrate) {
        try {
          return migrate(stored.value, stored.version);
        } catch (error) {
          // Always reported: the recovery below DISCARDS the user's persisted
          // value, and a silent reset is the failure `no-fallbacks.md` bans.
          console.error(`Failed to migrate persisted atom "${key}"`, error);
        }
      }

      return initialValue;
    },
    setItem: (key: string, value: T) => {
      baseStorage.setItem(key, {
        version,
        value,
      });
    },
    removeItem: (key: string) => {
      baseStorage.removeItem(key);
    },
  };
}
